<template>
  <FilterButton title="View Options" icon="mdi-format-columns" @close="save" data-testid="carrier-survey-view-options" data-test-activator="carrier-survey-view-options-activator">
    <template #menu="{ close }">
      <div class="menu">
        <h4>View Options</h4>
        <div class="fields">
          <div class="field">
            <h5>Labels</h5>
            <label>
              <input type="radio" v-model="selectedLabels" value="showAll" data-testid="carrier-survey-view-options-labels-show-all"/>
              <span>Show All</span>
            </label>
            <label>
              <input type="radio" v-model="selectedLabels" value="showNone" data-testid="carrier-survey-view-options-labels-show-none"/>
              <span>Show None</span>
            </label>
            <label>
              <input type="radio" v-model="selectedLabels" value="showSelected" data-testid="carrier-survey-view-options-labels-show-selected"/>
              <span>Show Only Selected</span>
            </label>
          </div>
          <div class="field">
            <h5>Carriers</h5>
            <label>
              <input type="radio" v-model="selectedCarriers" value="showSelected" data-testid="carrier-survey-view-options-carriers-show-selected"/>
              <span>Show Only Selected</span>
            </label>
            <label>
              <input type="radio" v-model="selectedCarriers" value="showAll" data-testid="carrier-survey-view-options-carriers-show-all"/>
              <span>Show All</span>
            </label>
          </div>
          <div class="field">
            <h5>Table Values</h5>
            <label>
              <input type="radio" v-model="selectedValueDisplay" value="show" data-testid="carrier-survey-view-options-values-show"/>
              <span>Show</span>
            </label>
            <label>
              <input type="radio" v-model="selectedValueDisplay" value="hide" data-testid="carrier-survey-view-options-values-hide"/>
              <span>Hide</span>
            </label>
          </div>
          <div class="field">
            <h5>Axis Scale</h5>
            <label>
              <input type="radio" v-model="selectedAxisDisplayMode" value="none" />
              <span>None</span>
            </label>
            <label>
              <input type="radio" v-model="selectedAxisDisplayMode" value="averages" />
              <span>Show Averages</span>
            </label>
            <label>
              <input type="radio" v-model="selectedAxisDisplayMode" value="all" />
              <span>Show Full</span>
            </label>
          </div>

          <div class="field">
            <h5>Bubble Size</h5>
            <label>
              <input type="radio" v-model="sizingDisplay" value="standardBubble" data-testid="carrier-survey-view-options-standard-sizing"/>
              <span>Standard</span>
            </label>
            <label>
              <input type="radio" v-model="sizingDisplay" value="countBubble" data-testid="carrier-survey-view-options-count-sizing"/>
              <span>Count Of Responses</span>
            </label>
            <label>
              <input type="radio" v-model="sizingDisplay" value="premiumBubble" data-testid="carrier-survey-view-options-premium-sizing"/>
              <span>Premium</span>
            </label>

            <div v-if="this.surveyType === 'D&O'">
              <label class="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-2 tw:items-center tw:content-center tw:py-1 tw:px-0 tw:cursor-pointer tw:select-none">
                <input type="radio" v-model="sizingDisplay" value="primaryPublic" data-testid="carrier-survey-view-options-primary-public"/>
                <span>Primary Public D&O</span>
              </label>
              <label class="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-2 tw:items-center tw:content-center tw:py-1 tw:px-0 tw:cursor-pointer tw:select-none">
                <input type="radio" v-model="sizingDisplay" value="primaryPrivate" data-testid="carrier-survey-view-options-primary-private"/>
                <span>Primary Private D&O</span>
              </label>
              <label class="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-2 tw:items-center tw:content-center tw:py-1 tw:px-0 tw:cursor-pointer tw:select-none">
                <input type="radio" v-model="sizingDisplay" value="leadSideA" data-testid="carrier-survey-view-options-lead-side-a"/>
                <span>Lead Side A</span>
              </label>
              <label class="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-2 tw:items-center tw:content-center tw:py-1 tw:px-0 tw:cursor-pointer tw:select-none">
                <input type="radio" v-model="sizingDisplay" value="excessDAndO" data-testid="carrier-survey-view-options-excess-d-and-o"/>
                <span>Excess D&O (Public & Private)</span>
              </label>
              <label v-if="showValueCACPartnershipOption" class="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-2 tw:items-center tw:content-center tw:py-1 tw:px-0 tw:cursor-pointer tw:select-none">
                <input type="radio" v-model="sizingDisplay" value="valueCACPartnership" data-testid="carrier-survey-view-options-value-cac-partnership"/>
                <span>Value the CAC Partnership</span>
              </label>
            </div>
          </div>
        </div>
        <div class="buttons">
          <v-btn size="small" variant="flat" color="primary" @click="close" data-testid="carrier-survey-view-options-apply">Done</v-btn>
        </div>
      </div>
    </template>
  </FilterButton>
</template>

<script>
import FilterButton from '@/components/FilterButton';

export default {
  name: 'CarrierSurveyViewOptions',
  components: { FilterButton },
  props: {
    labels: String,
    carriers: String,
    valueDisplay: String,
    bubbleSize: String,
    surveyType: String,
    axisDisplayMode: String,
    appliedYears: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return value.every((item) => typeof item === 'number');
      }
    },
  },
  data() {
    return {
      dirty: false,
      localSelectedLabels: undefined,
      localSelectedCarriers: undefined,
      localValueDisplay: undefined,
      bubbleSizing: undefined,
      localAxisDisplayMode: undefined,
    };
  },
  computed: {
    selectedLabels: {
      get() {
        return this.localSelectedLabels || this.labels || 'showAll';
      },
      set(newValue) {
        this.localSelectedLabels = newValue;
      }
    },
    selectedCarriers: {
      get() {
        return this.localSelectedCarriers || this.carriers || 'showAll';
      },
      set(newValue) {
        this.localSelectedCarriers = newValue;
      }
    },
    selectedValueDisplay: {
      get() {
        return this.localValueDisplay || this.valueDisplay || 'show';
      },
      set(newValue) {
        this.localValueDisplay = newValue;
      }
    },
    sizingDisplay: {
      get() {
        return this.bubbleSizing || this.bubbleSize || 'standardBubble';
      },
      set(newValue) {
        return this.bubbleSizing = newValue;
      }
    },
    selectedAxisDisplayMode: {
      get() {
        return this.localAxisDisplayMode || this.axisDisplayMode || 'none';
      },
      set(newValue) {
        this.localAxisDisplayMode = newValue;
      }
    },
    showValueCACPartnershipOption() {
      return this.appliedYears.includes(2023);
    },
  },
  methods: {
    save() {
      if (this.dirty) {
        this.dirty = false;
        this.$emit('change', this.selectedLabels, this.selectedCarriers, this.selectedValueDisplay, this.sizingDisplay, this.selectedAxisDisplayMode);
      }
      return true;
    }
  },
  watch: {
    selectedLabels() {
      this.dirty = true;
    },
    selectedCarriers() {
      this.dirty = true;
    },
    selectedValueDisplay() {
      this.dirty = true;
    },
    sizingDisplay() {
      this.dirty = true;
    },
    selectedAxisDisplayMode() {
      this.dirty = true;
    },
    appliedYears(newVal) {
      if (!newVal.includes(2023) && this.sizingDisplay === 'valueCACPartnership') {
        this.sizingDisplay = 'standardBubble'; 
        this.$emit('change', this.selectedLabels, this.selectedCarriers, this.selectedValueDisplay, this.sizingDisplay, this.selectedAxisDisplayMode);
      }
    },
  }
};
</script>

<style scoped>
.menu {
  padding: 1rem 0 0 0;
  min-width: 500px;
}

.menu > h4 {
  padding: 0 1rem;
}

.menu > .fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 1rem;
  padding: 0 1rem 1rem;
}

.menu > .fields > .field > h5 {
  font-size: 0.8rem;
  letter-spacing: 0.02em;
  margin: 1rem 0 0.5rem;
  text-transform: uppercase;
  font-weight: 500;
}

.menu > .fields > .field > label {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 0.5rem;
  align-items: center;
  align-content: center;
  padding: 0.2rem 0;
  cursor: pointer;
  user-select: none;
}

.menu > .buttons {
  padding: 1rem;
  border-top: 1px solid var(--v-border-base);
  background: var(--v-gray-lighten5);
  display: flex;
  justify-content: flex-end;
}
</style>
